import { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Wand2, FileText, ShieldAlert, ArrowRight, ArrowLeft, X, Sparkles } from "lucide-react";

const STEPS = [
    {
        title: "AI Weekly Digest",
        icon: Wand2,
        blurb: "Every Monday, Claude reads your live rankings + backlinks and writes a terse action list. Run it on demand from the Dashboard.",
        cta: "Take me to the Dashboard",
        route: "/app",
        cost: "3 credits",
    },
    {
        title: "Content Brief Generator",
        icon: FileText,
        blurb: "Type any target keyword. We pull the live top-10 SERP and Claude drafts an editor-ready brief with intent, format, headings, and target word count.",
        cta: "Open Content / AEO",
        route: "/app/content",
        cost: "5 credits",
    },
    {
        title: "Rank Drop Explainer + Bulk Diagnose",
        icon: ShieldAlert,
        blurb: "On the Keywords page, click the wand next to any keyword and Claude diagnoses why it moved — using the actual SERP competitors that took the spot.",
        cta: "Show me the Keywords page",
        route: "/app/keywords",
        cost: "2 credits",
    },
];

export function OnboardingTour() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);

    // Check if the user should see the tour (once per user)
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        client.get("/user/onboarding").then((r) => {
            if (!cancelled && !r.data.completed) setOpen(true);
        }).catch(() => { /* ignore */ });
        return () => { cancelled = true; };
    }, [user?.id]);

    const complete = async () => {
        setOpen(false);
        try { await client.post("/user/onboarding/complete", {}); } catch { /* ignore */ }
    };

    const next = () => {
        if (step < STEPS.length - 1) setStep(step + 1);
        else complete();
    };

    const finishAndGo = () => {
        const target = STEPS[step].route;
        complete();
        nav(target);
    };

    const s = STEPS[step];
    const Icon = s.icon;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) complete(); }}>
            <DialogContent
                data-testid="onboarding-tour"
                className="max-w-lg bg-background border border-primary/40 p-0 overflow-hidden"
            >
                <div className="bg-primary/10 border-b border-primary/30 px-6 py-4 flex items-center gap-3">
                    <div className="w-9 h-9 border border-primary/60 bg-primary/10 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1">
                        <div className="overline text-primary">WELCOME TO RANK FORGE</div>
                        <div className="text-sm font-mono text-muted-foreground mt-0.5">Quick tour — 30 seconds</div>
                    </div>
                    <button onClick={complete} className="text-muted-foreground hover:text-foreground p-1" aria-label="Close" data-testid="tour-skip">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-6 py-6">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 border border-primary/40 bg-primary/5 flex items-center justify-center text-primary">
                            <Icon className="w-5 h-5" />
                        </div>
                        <div>
                            <DialogTitle className="font-display text-2xl tracking-tight">{s.title}</DialogTitle>
                            <div className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest mt-0.5">{s.cost}</div>
                        </div>
                    </div>

                    <DialogDescription className="text-sm text-muted-foreground leading-relaxed mt-4" data-testid="tour-blurb">
                        {s.blurb}
                    </DialogDescription>

                    {/* Step dots */}
                    <div className="flex items-center gap-1.5 mt-6">
                        {STEPS.map((_, i) => (
                            <div key={i} className={`h-1 w-8 ${i === step ? "bg-primary" : i < step ? "bg-primary/40" : "bg-border"}`} />
                        ))}
                        <div className="ml-auto text-[10px] font-mono text-muted-foreground">{step + 1} / {STEPS.length}</div>
                    </div>

                    {/* Actions */}
                    <div className="mt-5 flex items-center gap-3">
                        {step > 0 && (
                            <button
                                onClick={() => setStep(step - 1)}
                                className="border border-border px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
                                data-testid="tour-back"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" /> Back
                            </button>
                        )}
                        <button
                            data-testid="tour-goto-btn"
                            onClick={finishAndGo}
                            className="flex-1 border border-primary/60 text-primary hover:bg-primary/10 px-4 py-2.5 text-sm font-medium"
                        >
                            {s.cta}
                        </button>
                        <button
                            data-testid="tour-next"
                            onClick={next}
                            className="bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90 flex items-center gap-1"
                        >
                            {step < STEPS.length - 1 ? "Next" : "Done"} <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
